import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n/i18n';

/**
 * Focus management across route changes (spec: "Preserve focus across route/overlay changes"):
 * after an in-app navigation the new page's h1 receives focus, so keyboard and screen-reader
 * users start at the new content. The first page load keeps the browser default (skip link first).
 */
export const NavigationFocusContext = createContext(false);

export function PageHeading({
  children,
  lang,
  hidden = false,
}: {
  children: ReactNode;
  lang?: string;
  /** Present for assistive technology and focus, but not drawn (e.g. over the 3D island). */
  hidden?: boolean;
}) {
  const focusOnMount = useContext(NavigationFocusContext);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusOnMount) heading.current?.focus();
  }, [focusOnMount]);
  return (
    <h1
      ref={heading}
      tabIndex={-1}
      className={hidden ? 'page-heading ui-visually-hidden' : 'page-heading'}
      lang={lang}
    >
      {children}
    </h1>
  );
}

/** Sets the document title as "<page> — Kids World". */
export function useDocumentTitle(page: string | null): void {
  const { t } = useI18n();
  const brand = t('shell.brand');
  useEffect(() => {
    document.title = page ? `${page} — ${brand}` : brand;
  }, [page, brand]);
}
